import React, { useState, useEffect } from "react";
import axios from "axios";
import { Favorite } from "@material-ui/icons";
import { auth } from "../firebase";
import "./Search.css";

const firestoreApiBaseUrl =
    "https://firestore.googleapis.com/v1/projects/scholarship-10c69/databases/(default)/documents";
const pageSize = 1000;

const Search = ({isDarkMode, toggleTheme}) => {
    const [allScholarships, setAllScholarships] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [countryFilter, setCountryFilter] = useState("");
    const [nextPageToken, setNextPageToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [userEmail, setUserEmail] = useState("");
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    const [setIsDarkMode] = useState(true);
    const [likedPosts, setLikedPosts] = useState({});
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [expandedScholarship, setExpandedScholarship] = useState(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUserEmail(user.email);
            } else {
                setUserEmail("");
            }
        });

        if (userEmail && !initialFetchDone) {
            fetchAllScholarships();
            setInitialFetchDone(true);
        }

        return () => unsubscribe();
    }, [userEmail, initialFetchDone]);
    const fetchAllScholarships = async () => {
        try {
            setLoading(true);

            if (!hasMoreData) {
                return;
            }

            let url = `${firestoreApiBaseUrl}/Scholarship?pageSize=${pageSize}`;

            if (nextPageToken) {
                url += `&pageToken=${nextPageToken}`;
            }

            const response = await axios.get(url);

            const fetchedScholarships = response.data.documents.map((doc) => ({
                id: doc.name.split("/").pop(),
                data: doc.fields,
            }));

            setAllScholarships((prevScholarships) => [
                ...prevScholarships,
                ...fetchedScholarships,
            ]);

            if (response.data.nextPageToken) {
                setNextPageToken(response.data.nextPageToken);
            } else {
                setNextPageToken(null);
                setHasMoreData(false);
            }
        } catch (error) {
            console.error("Error fetching scholarships: ", error);
        } finally {
            setLoading(false);
        }
    };
    const handleSearch = async (e) => {
        e.preventDefault();

        try {
            let url = `${firestoreApiBaseUrl}/Scholarship?pageSize=${pageSize}`;

            // Add a query for the search input
            if (searchInput.trim() !== "") {
                url += `&q=${encodeURIComponent(searchInput)}`;
            }
            // Add a query for the country filter
            if (countryFilter.trim() !== "") {
                url += `&q=country:${encodeURIComponent(countryFilter)}`;
            }
            const response = await axios.get(url);

            const searchedScholarships = response.data.documents.map((doc) => ({
                id: doc.name.split("/").pop(),
                data: doc.fields,
            }));

            setAllScholarships(searchedScholarships);
        } catch (error) {
            console.error("Error searching scholarships: ", error);
            console.error(
                "Firestore API error response: ",
                error.response?.data
            );
        }
    };

    const applySearch = (docs) => {
        if (searchInput.trim() === "" && countryFilter.trim() === "") {
            return docs; // If both search input and country filter are empty, return all documents
        } else {
            return docs.filter(
                (doc) =>
                    doc.data.country.stringValue
                        .toLowerCase()
                        .includes(countryFilter.toLowerCase()) &&
                    doc.data.title.stringValue
                        .toLowerCase()
                        .includes(searchInput.toLowerCase())
            );
        }
    };
    const toggleExpand = (scholarshipId) => {
        setExpandedScholarship((prevExpanded) => (prevExpanded === scholarshipId ? null : scholarshipId));
    };
    const buttonStyle = {
        color: isDarkMode ? 'black' : 'ghostwhite',
        backgroundColor: isDarkMode ? '#f8f9fa' : '#333'
    };
    const handleLike = async (scholarshipId) => {
        try {
            const scholarshipToLike = allScholarships.find((s) => s.id === scholarshipId);

            const requestData = {
                fields: {
                    scholarshipId: { stringValue: scholarshipId },
                    userEmail: { stringValue: userEmail },
                    scholarshipDetails: {
                        mapValue: {
                            fields: {
                                title: { stringValue: scholarshipToLike.data.title?.stringValue || "" },
                                description: { stringValue: scholarshipToLike.data.description?.stringValue || "" },
                                eligibility: { stringValue: scholarshipToLike.data.eligibility?.stringValue || "" },
                                deadline: { stringValue: scholarshipToLike.data.deadline?.stringValue || "" },
                                link: { stringValue: scholarshipToLike.data.link?.stringValue || "" },
                                link1: { stringValue: scholarshipToLike.data.link1?.stringValue || "" }, // Add link field
                                country: { stringValue: scholarshipToLike.data.country?.stringValue || "" },
                                imageUrl: { stringValue: scholarshipToLike.data.imageUrl?.stringValue || "" },
                            },
                        },
                    },
                },
            };

            // Toggle the liked status for the current scholarship
            setLikedPosts((prevLikedPosts) => ({
                ...prevLikedPosts,
                [scholarshipId]: !prevLikedPosts[scholarshipId],
            }));

            await axios.post(
                `${firestoreApiBaseUrl}/bookmark`,
                requestData,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            console.log(`Liked scholarship with ID: ${scholarshipId} for user: ${userEmail}`);
        } catch (error) {
            console.error("Error liking scholarship: ", error);

            if (error.response) {
                console.error("Firestore error data:", error.response.data);
                console.error("Firestore error status:", error.response.status);
                console.error("Firestore error headers:", error.response.headers);
            } else if (error.request) {
                console.error("No response received from Firestore");
            } else {
                console.error("Error setting up the request:", error.message);
            }
        }
    };

    return (
        <div className={`container mt-5 ${isDarkMode ? 'dark-mode' : 'light-mode'}`} style={{ backgroundColor: isDarkMode ? '#333' : '#f8f9fa' ,minHeight:"100vh"}}>
            <h2 className="mb-4 text-center" style={{ fontSize: "1.5rem" }}>Search Scholarships</h2>
            <form onSubmit={handleSearch} className="mb-3">
                <div className="row">
                    <div className="col-md text-center">
                        <select
                            className="form-select"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            style={{
                                width: "110%", // Adjust width for smaller screens
                                backgroundColor: isDarkMode ? '#f8f9fa' : '#333',
                                color: isDarkMode ? '#000' : '#fff' // Set text color to white in light mode
                            }}
                        >
                            <option value="">Select Keyword</option>
                            <option value="scholarship">Scholarship</option>
                            <option value="Education">Education</option>
                            <option value="Fully Funded">Fully Funded</option>
                            <option value="Citizen">Citizenship</option>
                            <option value="Partial Funded">Partial Funded</option>
                            <option value="internship">Internship</option>
                            <option value="fellowship">Fellowship</option>
                            {/* Add more options as needed */}

                        </select>
                    </div>
                    <div className="col-md">
                        <input
                            style={{
                                width: "110%", // Adjust width for smaller screens
                                backgroundColor: isDarkMode ? '#f8f9fa' : '#333',
                                color: isDarkMode ? '#000' : '#fff' // Set text color to white in light mode
                            }}
                            type="text"
                            className={`form-control ${isDarkMode ? '' : 'placeholder-color'}`}
                            placeholder="Country Name"
                            value={countryFilter}
                            onChange={(e) => setCountryFilter(e.target.value)}

                        />
                    </div>

                    <div className="col-md">


                        <button type="submit" className="btn" style={buttonStyle}>
                            Search
                        </button>

                    </div>
                </div>
            </form>

            {applySearch(allScholarships).map(({ id, data }) => (
                <div key={id} className="row mb-4">
                    <div className="col-md">
                        <div className="card mb-4">
                            {data.imageUrl && (
                                <img
                                    src={data.imageUrl.stringValue}
                                    alt="Scholarship"
                                    className="card-img-top img-fluid"
                                />
                            )}
                            <div className="card-body" style={{ maxWidth: "100%" }}>
                                <h4 className="card-title mb-0 fw-bold" style={{ overflowWrap: "break-word", maxWidth: "100%" ,textAlign: "left"  }}>{data.title?.stringValue}</h4>
                                <p className="card-text" style={{ overflowWrap: "break-word", textAlign: "left" }}>
                                    {expandedScholarship === id ? data.description?.stringValue : `${data.description?.stringValue.slice(0, 100)}...`}
                                </p>
                                {expandedScholarship === id && (
                                    <React.Fragment>
                                        <p className="card-text" style={{ overflowWrap: "break-word", textAlign: "left" }}>
                                            <strong>Eligibility:</strong>{" "}{data.eligibility?.stringValue}
                                        </p>
                                        <p className="card-text" style={{ overflowWrap: "break-word", textAlign: "left" }}>
                                            <strong>Deadline:</strong>{" "}{data.deadline?.stringValue}
                                        </p>
                                        <p className="card-text" style={{ overflowWrap: "break-word", textAlign: "left" }}>
                                            <strong>Country:</strong>{" "}{data.country?.stringValue}
                                        </p>
                                        {/* Render the link if it exists */}
                                        {data.link && (
                                            <div className="card-text" style={{ overflowWrap: "break-word", textAlign: "left" }}>
                                                <strong>Link:</strong>{" "}
                                                <a
                                                    href={data.link.stringValue}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {data.link.stringValue}
                                                </a>
                                            </div>
                                        )}
                                        {data.link1 && data.link1.stringValue.trim() !== "" && (
                                            <div className="card-text" style={{ overflowWrap: "break-word", textAlign: "left" }}>
                                                <strong>Link:</strong>{" "}
                                                <a
                                                    href={data.link1.stringValue}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {data.link1.stringValue}
                                                </a>
                                            </div>
                                        )}
                                    </React.Fragment>
                                )}
                                <div className="text-end" style={{ marginTop: "4%" }}> {/* Align to the right */}
                                    <button
                                        className={`read-more-button btn btn-secondary ${window.innerWidth < 576 ? 'btn-sm' : ''}`} // Apply btn-sm class when screen width is less than 576px
                                        onClick={() => toggleExpand(id)}
                                        style={{
                                            transition: 'opacity 1.0s ease',
                                            backgroundColor: isDarkMode ? "#333" : "#333",
                                            color: isDarkMode ? "white" : "white",
                                        }}
                                    >
                                        {expandedScholarship === id ? "Read less" : "Read more"}
                                    </button>
                                </div>
                                <div className="text-center">
                                    <button
                                        className={`like-button btn btn-secondary ${likedPosts[id] ? 'liked' : ''}`}
                                        style={{
                                            backgroundColor: (likedPosts[id] || isBookmarked) ? 'white' : 'white',
                                            color: (likedPosts[id] || isBookmarked) ? 'white' : 'whitesmoke'
                                        }}
                                        onClick={() => handleLike(id)}
                                    >
                                        <Favorite style={{ color: (likedPosts[id] || isBookmarked) ? 'red' : 'black' }} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}

            {hasMoreData && !loading && (
                <button className="btn btn-primary btn-block mt-3" onClick={fetchAllScholarships}>
                    Load More
                </button>
            )}

            {loading && <p className="text-center">Loading...</p>}
        </div>



    );
};

export default Search;
